<template>
  <header class="c-head">
    <!-- Título + ações -->
    <div class="page-head">
      <div class="ph-titles">
        <h1 class="page-title">{{ headerTitle }}</h1>
        <div class="sub">
          {{
            $t('CONTACTS_PAGE.CONTACTS_COUNT', { count: formattedCount })
          }}
        </div>
      </div>

      <div class="hactions">
        <div v-if="hasActiveSegments" class="seg-actions">
          <woot-button
            variant="clear"
            color-scheme="secondary"
            icon="edit"
            @click="onToggleEditSegmentsModal"
          >
            {{ $t('CONTACTS_PAGE.FILTER_CONTACTS_EDIT') }}
          </woot-button>
          <woot-button
            variant="clear"
            color-scheme="alert"
            icon="delete"
            @click="onToggleDeleteSegmentsModal"
          >
            {{ $t('CONTACTS_PAGE.FILTER_CONTACTS_DELETE') }}
          </woot-button>
        </div>

        <woot-button
          v-if="isAdmin && !isPageCampaignsFlow"
          class="btn-ghost"
          variant="clear"
          color-scheme="secondary"
          icon="upload"
          @click="onToggleImport"
        >
          {{ $t('IMPORT_CONTACTS.BUTTON_LABEL') }}
        </woot-button>
        <woot-button
          v-if="isAdmin && !isPageCampaignsFlow"
          class="btn-ghost"
          variant="clear"
          color-scheme="secondary"
          icon="arrow-download"
          @click="onExportSubmit"
        >
          {{ $t('EXPORT_CONTACTS.BUTTON_LABEL') }}
        </woot-button>
        <woot-button
          v-if="isAdmin && !isPageCampaignsFlow"
          class="btn-ghost btn-danger"
          variant="clear"
          color-scheme="alert"
          icon="delete"
          :disabled="isContactsDelete"
          @click="onToggleDeleteAccounts"
        >
          {{ $t('DELETE_CONTACTS.CONFIRM') }}
        </woot-button>

        <woot-button
          v-if="isPageCampaignsFlow"
          color-scheme="info"
          icon="save"
          @click="handleContactsList"
        >
          {{ $t('CAMPAIGN_FLOW.LIST_CONTACTS.CREATE.FORM.BUTTON_SAVE') }}
        </woot-button>

        <woot-button
          v-if="!isPageCampaignsFlow"
          class="btn-create"
          icon="person-add"
          data-testid="create-new-contact"
          @click="onToggleCreate"
        >
          {{ $t('CREATE_CONTACT.BUTTON_LABEL') }}
        </woot-button>
      </div>
    </div>

    <!-- Abas -->
    <div class="segs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="seg"
        :class="{ active: isActiveTab(tab) }"
        @click="goToTab(tab)"
      >
        {{ tab.label }}
        <span v-if="isActiveTab(tab)" class="cnt">{{ formattedCount }}</span>
      </button>
    </div>

    <!-- Toolbar: filtros + ordenação (página de contatos).
         A busca inline saiu daqui: na página de contatos usa-se a busca global
         da topbar; nos modais de seleção (picker) mostra-se só a busca abaixo. -->
    <div
      v-if="!isPageCampaignsFlow && !enablePickerSearch"
      class="toolbar"
    >
      <button
        v-if="!hasActiveSegments"
        class="tbtn"
        :class="{ 'is-active': hasAppliedFilters }"
        @click="onToggleFilter"
      >
        <fluent-icon icon="filter" size="16" />
        {{ $t('CONTACTS_PAGE.FILTER_CONTACTS') }}
        <span v-if="hasAppliedFilters" class="filter-dot" />
      </button>

      <woot-button
        v-if="hasAppliedFilters && !hasActiveSegments"
        variant="clear"
        color-scheme="secondary"
        icon="save"
        class="tbtn-save"
        @click="onToggleSegmentsModal"
      >
        {{ $t('CONTACTS_PAGE.FILTER_CONTACTS_SAVE') }}
      </woot-button>

      <div class="spacer" />

      <div v-on-clickaway="closeSortMenu" class="sort-wrap">
        <button class="tbtn" @click="toggleSortMenu">
          {{ currentSortLabel }}
          <fluent-icon icon="chevron-down" size="16" />
        </button>
        <div v-if="showSortMenu" class="sort-menu">
          <button
            v-for="opt in sortOptions"
            :key="opt.key"
            class="sort-item"
            :class="{ active: opt.key === activeSortKey }"
            @click="selectSort(opt)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Busca de contatos nos modais de seleção (picker): criar lista de
         campanha, iniciar nova conversa, etc. Habilitada pelo componente que
         embute o header (via is-picker → enable-picker-search). -->
    <div v-if="enablePickerSearch" class="toolbar">
      <label class="c-search">
        <fluent-icon icon="search" size="16" class="c-search-ic" />
        <input
          type="text"
          :placeholder="$t('CONTACTS_PAGE.SEARCH_INPUT_PLACEHOLDER')"
          :value="searchQuery"
          @keyup.enter="onSearchSubmit"
          @input="onInputSearch"
        />
      </label>
    </div>
  </header>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import adminMixin from 'dashboard/mixins/isAdmin';

export default {
  mixins: [adminMixin, clickaway],
  props: {
    headerTitle: {
      type: String,
      default: '',
    },
    searchQuery: {
      type: String,
      default: '',
    },
    // Exibe apenas o campo de busca (modo picker: modais de seleção de
    // contato, como criar lista de campanha ou iniciar nova conversa).
    enablePickerSearch: {
      type: Boolean,
      default: false,
    },
    totalCount: {
      type: [String, Number],
      default: 0,
    },
    segmentsId: {
      type: [String, Number],
      default: 0,
    },
    onInputSearch: {
      type: Function,
      default: () => {},
    },
    onSearchSubmit: {
      type: Function,
      default: () => {},
    },
    onToggleCreate: {
      type: Function,
      default: () => {},
    },
    onToggleImport: {
      type: Function,
      default: () => {},
    },
    handleContactsList: {
      type: Function,
      default: () => {},
    },
    onToggleDeleteAccounts: {
      type: Function,
      default: () => {},
    },
    isContactsDelete: {
      type: Boolean,
      default: false,
    },
    onExportSubmit: {
      type: Function,
      default: () => {},
    },
    onToggleFilter: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      showSortMenu: false,
      activeSortKey: 'last_activity_at',
    };
  },
  computed: {
    tabs() {
      return [
        {
          key: 'all',
          name: 'contacts_dashboard',
          label: this.$t('CONTACTS_PAGE.TAB_ALL'),
        },
        {
          key: 'wallet',
          name: 'wallet_contacts_dashboard',
          label: this.$t('CONTACTS_PAGE.TAB_WALLET'),
        },
      ];
    },
    sortOptions() {
      return [
        {
          key: 'last_activity_at',
          order: 'desc',
          label: this.$t('CONTACTS_PAGE.SORT.LAST_ACTIVITY'),
        },
        {
          key: 'name',
          order: 'asc',
          label: this.$t('CONTACTS_PAGE.SORT.NAME'),
        },
        {
          key: 'created_at',
          order: 'desc',
          label: this.$t('CONTACTS_PAGE.SORT.CREATED_AT'),
        },
      ];
    },
    ...mapGetters({
      getAppliedContactFilters: 'contacts/getAppliedContactFilters',
      accountId: 'getCurrentAccountId',
    }),
    isPageCampaignsFlow() {
      return this.$route.name === 'campaigns_flow';
    },
    hasAppliedFilters() {
      return this.getAppliedContactFilters.length;
    },
    hasActiveSegments() {
      return this.segmentsId !== 0;
    },
    formattedCount() {
      const n = Number(this.totalCount) || 0;
      return n.toLocaleString('pt-BR');
    },
    currentSortLabel() {
      const opt = this.sortOptions.find(o => o.key === this.activeSortKey);
      return opt ? opt.label : this.$t('CONTACTS_PAGE.SORT_BY');
    },
  },
  methods: {
    isActiveTab(tab) {
      if (tab.key === 'wallet') {
        return this.$route.name === 'wallet_contacts_dashboard';
      }
      return this.$route.name !== 'wallet_contacts_dashboard';
    },
    goToTab(tab) {
      if (this.isActiveTab(tab)) return;
      this.$router.push({
        name: tab.name,
        params: { accountId: this.accountId },
      });
    },
    onToggleSegmentsModal() {
      this.$emit('on-toggle-save-filter');
    },
    onToggleEditSegmentsModal() {
      this.$emit('on-toggle-edit-filter');
    },
    onToggleDeleteSegmentsModal() {
      this.$emit('on-toggle-delete-filter');
    },
    toggleSortMenu() {
      this.showSortMenu = !this.showSortMenu;
    },
    closeSortMenu() {
      this.showSortMenu = false;
    },
    selectSort(opt) {
      this.activeSortKey = opt.key;
      this.showSortMenu = false;
      this.$emit('on-sort-change', { [opt.key]: opt.order });
    },
  },
};
</script>

<style lang="scss" scoped>
.c-head {
  padding: 0 2.4rem;
  background: var(--white);
}

/* título + ações */
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.6rem;
  padding: 2rem 0 1.4rem;
}
.ph-titles {
  min-width: 0;
}
.page-title {
  font-size: 2.1rem;
  font-weight: 800;
  letter-spacing: -0.03rem;
  color: #002136;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.sub {
  font-size: 1.25rem;
  color: #8a97a3;
  font-weight: 500;
  margin-top: 0.2rem;
}
.hactions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-shrink: 0;
}
.seg-actions {
  display: flex;
  gap: 0.4rem;
}

.c-head::v-deep .btn-ghost {
  height: 3.8rem;
  border: 1px solid #d3dbe2;
  border-radius: 0.9rem;
  color: #516170;
  font-weight: 600;

  &:hover {
    background: #f0f3f5;
  }
}
.c-head::v-deep .btn-ghost.btn-danger {
  color: #dc4b4b;
  &:hover {
    background: #fdecec;
  }
}
.c-head::v-deep .btn-create {
  height: 3.8rem;
  background: #01c6d8;
  color: #fff;
  border-radius: 0.9rem;
  font-weight: 700;

  &:hover {
    background: #03b2c3;
  }
  .icon,
  svg {
    color: #fff;
  }
}

/* abas */
.segs {
  display: flex;
  gap: 0.4rem;
  border-bottom: 1px solid #e4e9ee;
}
.seg {
  padding: 0.9rem 1.4rem;
  font-size: 1.3rem;
  font-weight: 600;
  color: #516170;
  border: 0;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  cursor: pointer;
  transition: 0.15s;
}
.seg:hover {
  color: #0f1f2a;
}
.seg.active {
  color: #0a7581;
  border-color: #01c6d8;
}
.seg .cnt {
  font-size: 1.1rem;
  font-weight: 700;
  color: #8a97a3;
  background: #f0f3f5;
  padding: 0.1rem 0.7rem;
  border-radius: 1rem;
}
.seg.active .cnt {
  background: #e1f8fb;
  color: #0a7581;
}

/* toolbar */
.toolbar {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1.4rem 0;
}
.c-search {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  background: var(--white);
  border: 1px solid #e4e9ee;
  border-radius: 0.9rem;
  padding: 0 1.2rem;
  height: 3.8rem;
  width: 29rem;
  margin: 0;
  transition: 0.15s;

  &:focus-within {
    border-color: #01c6d8;
    box-shadow: 0 0 0 3px #e1f8fb;
  }
  .c-search-ic {
    color: #8a97a3;
    flex: none;
  }
  input {
    border: 0;
    background: none;
    outline: none;
    flex: 1;
    font-size: 1.3rem;
    color: #0f1f2a;
    margin: 0;
    height: auto;
    padding: 0;
    box-shadow: none;
    &::placeholder {
      color: #8a97a3;
    }
  }
}
.tbtn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  height: 3.8rem;
  padding: 0 1.2rem;
  background: var(--white);
  border: 1px solid #e4e9ee;
  border-radius: 0.9rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #516170;
  cursor: pointer;
  transition: 0.15s;

  &:hover {
    border-color: #d3dbe2;
    color: #0f1f2a;
  }
  &.is-active {
    border-color: #01c6d8;
    color: #0a7581;
  }
}
.filter-dot {
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
  background: #01c6d8;
}
.spacer {
  flex: 1;
}
.sort-wrap {
  position: relative;
}
.sort-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  min-width: 18rem;
  background: var(--white);
  border: 1px solid #e4e9ee;
  border-radius: 1rem;
  box-shadow: 0 12px 40px rgba(16, 32, 45, 0.16);
  padding: 0.4rem;
  z-index: 30;
}
.sort-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.8rem 1rem;
  border: 0;
  background: transparent;
  border-radius: 0.7rem;
  font-size: 1.3rem;
  color: #0f1f2a;
  cursor: pointer;

  &:hover {
    background: #f0f3f5;
  }
  &.active {
    background: #e1f8fb;
    color: #0a7581;
    font-weight: 600;
  }
}
.c-head::v-deep .tbtn-save {
  height: 3.8rem;
  border-radius: 0.9rem;
}
</style>
